import React from 'react';
import { omitFromObject } from 'utils/helpers';
import noop from 'utils/noop';
import Tag from 'core/tag';
import Item from './Item';
import {
  StyledSemanticInput,
  StyledSemanticButtonGroup,
  StyledSemanticDropdown,
  StyledSemanticDropdownDivider,
  StyledSemanticDropdownMenu,
  Trigger,
} from './MultiSelect.styled';

// type Props = {
//   /** Placholder text of filter search input in dropdown. */
//   filterPlaceholder: ?string,
//   /** Name of option. Shown when option is selected. e.g. 1 "Option" Selected. */
//   optionName: string,
//   /** Plural name of options. Shown when options are selected. e.g. 3 "Options" Selected. */
//   optionsName: string,
//   /**
//    * Callback function when number of selected options change.
//    *
//    * ```
//    * onChange(value) => {}
//    * ```
//    * @param { Array } value: The newly selected values.
//    * @returns { void }
//    */
//   onChange: () => void,
//   /** Array of option objects { text: display text, value: value of option } */
//   options: [],
//   /**
//    * Amount of items selected before showing selected label. Default: 5
//    * Set to -1 to always show a list of selected items.
//    * Set to 0 to always show selected label.
//    */
//   overflowAmount: number,
//   /** Placeholder text when no options are selected. */
//   placeholder: string,
//   /** Label to show to indicate options selected. e.g. 3 Options "Selected". */
//   selectedLabel: string,
//   /** Array of selected values. */
//   value: [?string],
//   /**
//    * Function for custom filtering functionality
//    *
//    * ```
//    * onChange(options, filter) => []
//    * ```
//    * @param { Array } options: Dropdown options to filter on.
//    * @param { string } filter: Filter input.
//    * @returns { void }
//    */
//   search: ?(object[], string) => void,
// };

export default class MultiSelect extends React.Component {
  static defaultProps = {
    filterPlaceholder: 'Filter',
    onChange: noop,
    optionName: 'Item',
    optionsName: 'Items',
    options: [],
    overflowAmount: 5,
    placeholder: 'Select...',
    selectedLabel: 'Selected',
    value: [],
  };

  constructor(props) {
    super(props);
    this.state = {
      open: false,
      initialFocus: false,
      filter: '',
      filterRef: {},
    };
  }

  /**
   * onMount
   * Get the reference of search filter when component mounts.
   * @param { object } filterRef: Reference to the input.
   * @return { void }
   */
  onMount = (filterRef) => {
    this.setState({ filterRef });
  };

  /**
   * handleSelectAll
   * Callback function when select all options is clicked. Toggles select of all options.
   * @param { object } selectAllOptions: Data object of select all item containing selected state.
   * @return { void }
   */
  handleSelectAll = ({ selected }) => {
    const { onChange, options } = this.props;
    if (selected) {
      onChange([]);
    } else {
      onChange(options.map(({ value }) => value));
    }
  };

  /**
   * handleBlur
   * Callback function when dropdown is blurred. Closes dropdown if filter is not initially focused.
   * @return { void }
   */
  handleBlur = () => {
    if (this.state.initialFocus) {
      this.setState({ initialFocus: false });
    } else {
      this.setState({ open: false });
    }
  };

  /**
   * handleDeleteTag
   * Callback function when tag is deleted.
   * @param { Object } itemParams:
   * @return { void }
   */
  handleDeleteTag = ({ value: tagValue }) => {
    const { onChange, value } = this.props;
    onChange(value.filter((val) => val !== tagValue));
  };

  /**
   * handleClickItem
   * Callback function when item is clicked.
   * @param { Object } itemParams: item params containing selected state and value.
   * @return { void }
   */
  handleClickItem = ({ selected, value: itemValue }) => {
    const { onChange, value } = this.props;
    if (selected) {
      onChange(value.filter((val) => val !== itemValue));
    } else {
      onChange([...value, itemValue]);
    }
  };

  /**
   * handleClose
   * Callback function when dropdown closes. Only close dropdown if clicking on trigger
   * or outside of the dropdown.
   * @param { object } e: The close event.
   * @return { void }
   */
  handleClose = (e) => {
    if (e && e.target) {
      if (e.target.attributes[0] && e.target.attributes[0].value === 'listbox') {
        this.setState({ open: false });
      }
    } else {
      this.setState({ open: false });
    }
  };

  /**
   * handleOpen
   * Callback function when dropdown opens
   * @return { void }
   */
  handleOpen = () => {
    const { filterRef } = this.state;
    this.setState({ open: true, initialFocus: true, filter: '' });
    setTimeout(() => {
      if (
        filterRef &&
        filterRef.inputRef &&
        filterRef.inputRef.current &&
        filterRef.inputRef.current.focus
      ) {
        filterRef.inputRef.current.focus();
      }
    });
  };

  /**
   * handleFilterChange
   * Callback function when filter search input value changes.
   * @param { Object } e: The change event.
   * @param { Object } filterProps: The filter input props containing the updated value.
   * @return { void }
   */
  handleFilterChange = (e, { value }) => {
    this.setState({ filter: value });
  };

  render() {
    const {
      filterPlaceholder,
      options,
      placeholder,
      optionName,
      optionsName,
      overflowAmount,
      search,
      selectedLabel,
      value,
      ...otherProps
    } = this.props;
    const { filter, open } = this.state;

    /* Filters options to show based on filter string entered. */
    const filteredOptions = search
      ? search(options, filter.toLowerCase())
      : options.filter(
          ({ text, value: optionValue }) =>
            optionValue.includes(filter.toLowerCase()) ||
            text.toLowerCase().includes(filter.toLowerCase()),
        );

    /* Creates the dropdown items. */
    const items = filteredOptions.map(({ text, subText, value: optionValue }, index) => (
      <Item
        key={`${value}${index}`}
        onClick={this.handleClickItem}
        selected={value.indexOf(optionValue) !== -1}
        text={text}
        subtext={subText}
        value={optionValue}
      />
    ));

    /* Determines what label(s) or tag(s) to show in trigger button. */
    let hasTags = false;
    let buttonText;

    if (overflowAmount === 0 || (value.length >= overflowAmount && !(overflowAmount < 0))) {
      // Show selected label if there is a value, else show the placeholder.
      buttonText = value.length
        ? `${value.length} ${value.length > 1 ? optionsName : optionName} ${selectedLabel}`
        : '';
    } else {
      // Show tags if there is a value, else show the placeholder.
      hasTags = true;
      buttonText = value.length
        ? options
            .filter(({ value: optionValue }) => value.indexOf(optionValue) !== -1)
            .map(({ text, value: tagValue }, index) => (
              <Tag
                key={`${text}${index}`}
                onDelete={this.handleDeleteTag}
                text={text}
                value={tagValue}
              />
            ))
        : '';
    }

    const trigger = (
      <Trigger data-tags={(!!hasTags && !!buttonText) || undefined}>
        {buttonText || placeholder}
      </Trigger>
    );

    return (
      <StyledSemanticButtonGroup basic fluid>
        <StyledSemanticDropdown
          button
          closeOnBlur={false}
          icon={null}
          onBlur={this.handleBlur}
          onClick={this.handleOpen}
          onClose={this.handleClose}
          onKeyDown={this.handleKeyDown}
          open={open}
          pointing
          trigger={trigger}
          {...omitFromObject(otherProps, 'onChange')}
        >
          <StyledSemanticDropdownMenu>
            <StyledSemanticInput
              icon="search"
              iconPosition="left"
              onChange={this.handleFilterChange}
              placeholder={filterPlaceholder}
              ref={this.onMount}
              tabIndex={1}
              value={filter}
            />
            <StyledSemanticDropdownDivider />
            <Item
              indeterminate={value.length > 0 && value.length < options.length}
              onClick={this.handleSelectAll}
              selected={value.length === options.length}
              text="Select All"
              value=""
            />
            <StyledSemanticDropdownDivider />
            <StyledSemanticDropdownMenu $isOptionsMenu scrolling>
              {items}
            </StyledSemanticDropdownMenu>
          </StyledSemanticDropdownMenu>
        </StyledSemanticDropdown>
      </StyledSemanticButtonGroup>
    );
  }
}
